/**
 * My Generations Controller
 * Handles the display and management of user's story generations
 */
// Check if class already exists to prevent duplicate declaration
if (typeof MyGenerationsController === 'undefined') {
  window.MyGenerationsController = class MyGenerationsController {
    constructor() {
      this.generations = [];
      this.isLoading = false;
      this.init();
    }

    async init() {
      try {
        // Check if required DOM elements exist
        const requiredElements = [
          'generations-loading',
          'generations-empty', 
          'generations-table',
          'generations-error'
        ];
        
        const missingElements = requiredElements.filter(id => !document.getElementById(id));
        
        if (missingElements.length > 0) {
          console.warn('MyGenerationsController: Missing required DOM elements:', missingElements);
          return;
        }
        
        this.showLoading();
        
        // Get generations from local storage
        this.generations = this.getGenerationsFromLocalStorage();
        
        if (this.generations.length === 0) {
          this.showEmpty();
          return;
        }

        // Fetch details for each generation with better error handling
        await this.fetchGenerationsDetails();
        
        // Get current cart to check for existing items
        await this.getCurrentCart();
        
        // Render the table
        this.renderTable();
        
      } catch (error) {
        console.error('MyGenerationsController init error:', error);
        this.showError();
      }
    }

    getGenerationsFromLocalStorage() {
      try {
        // Check if localStorage is available (handles sandboxed environments)
        if (typeof localStorage === 'undefined' || !localStorage) {
          return [];
        }
        
        const stored = localStorage.getItem('snaps_generations');
        if (stored) {
          const generations = JSON.parse(stored);
          return generations;
        }
        return [];
      } catch (error) {
        // Return empty array as fallback
        return [];
      }
    }

    async fetchGenerationsDetails() {
      const promises = this.generations.map(async (generation, index) => {
        try {
          const response = await fetch(`https://api.bysnaps.ai/preview-stories/previewes/shopify/${generation.id}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              return {
                ...generation,
                details: data.data
              };
            } else {
              return {
                ...generation,
                details: null
              };
            }
          } else {
            return {
              ...generation,
              details: null
            };
          }
          
        } catch (error) {
          return {
            ...generation,
            details: null
          };
        }
      });

      this.generations = await Promise.all(promises);
    }

    async getCurrentCart() {
      try {
        const response = await fetch(localeUrl('cart.js'));
        
        if (response.ok) {
          const cartData = await response.json();
          this.currentCart = cartData;
          
          // Extract generation IDs from cart items
          this.cartGenerationIds = cartData.items
            .filter(item => item.properties && item.properties['Generation ID'])
            .map(item => item.properties['Generation ID']);
        } else {
          this.currentCart = null;
          this.cartGenerationIds = [];
        }
      } catch (error) {
        this.currentCart = null;
        this.cartGenerationIds = [];
      }
    }

    renderTable() {
      const tableBody = document.getElementById('generations-table-body');
      if (!tableBody) {
        console.warn('MyGenerationsController: Table body element not found');
        this.showError();
        return;
      }

      // Clear existing content
      tableBody.innerHTML = '';

      // Filter out generations without details
      const validGenerations = this.generations.filter(g => g.details);
      
      if (validGenerations.length === 0) {
        this.showEmpty();
        return;
      }

      // Add rows for each generation
      validGenerations.forEach(generation => {
        const row = this.createTableRow(generation);
        tableBody.appendChild(row);
      });

      this.showTable();
    }

    createTableRow(generation) {
      const S = window.themeStrings.myGenerations;
      const row = document.createElement('tr');
      
      // Get cover image (sceneOrder: "0")
      const coverScene = generation.details.generated_scenes?.find(scene => scene.sceneOrder === "0");
      const coverImageUrl = coverScene?.processedImageUrl || '';
      
      // Helper function to build full image URL
      const buildImageUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        return `https://api.bysnaps.ai${url}`;
      };
      
      // Check if this generation is already in the cart
      const isInCart = this.cartGenerationIds && this.cartGenerationIds.includes(generation.id);
      const cartButtonDisabled = isInCart ? 'disabled' : '';
      const cartButtonClass = isInCart ? 'action-button action-button--cart action-button--disabled' : 'action-button action-button--cart';
      const cartButtonText = isInCart ? S.inCart : S.addToCart;
      
      row.innerHTML = `
        <td>${generation.details.kid_name || S.unknown}</td>
        <td>
          <img src="${buildImageUrl(generation.details.kid_image)}" alt="${S.kidAlt}" class="kid-image" onerror="this.style.display='none'">
        </td>
        <td>${generation.details.story_name || S.unknownStory}</td>
        <td>
          <span class="status-badge status-${generation.details.status || 'unknown'}">
            ${generation.details.status || S.unknown}
          </span>
        </td>
        <td>
          <img src="${buildImageUrl(coverImageUrl)}" alt="${S.coverAlt}" class="cover-image" onerror="this.style.display='none'">
        </td>
        <td>
          <span class="generation-id">${generation.id}</span>
        </td>
        <td>
          <div class="actions-column">
            <button class="action-button action-button--view" onclick="window.myGenerationsController.viewStory('${generation.id}')">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
              </svg>
              ${S.viewStory}
            </button>
            <button class="${cartButtonClass}" ${cartButtonDisabled} onclick="${!isInCart ? `window.myGenerationsController.addToCart('${generation.id}')` : ''}" title="${isInCart ? S.alreadyInCartTitle : S.addToCartTitle}">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L9.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
              </svg>
              ${cartButtonText}
            </button>
            <button class="action-button action-button--share" onclick="window.myGenerationsController.shareStory('${generation.id}')">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
              </svg>
              ${S.share}
            </button>
          </div>
        </td>
      `;
      
      return row;
    }

    viewStory(generationId) {
      const generation = this.generations.find(g => g.id === generationId);
      if (!generation?.details) {
        this.showNotification(window.themeStrings.myGenerations.storyUnavailable, 'error');
        return;
      }

      // Redirect to previewer page with generation ID
      const previewerUrl = `${localeUrl("pages/preview")}?generation_id=${generationId}`;
      window.location.href = previewerUrl;
    }



    async getDefaultVariantId() {
      try {
        // This is a fallback method - in a real implementation, you'd want to store the correct variant ID
        const response = await fetch(canonicalUrl('products.json?limit=1'));
        if (response.ok) {
          const data = await response.json();
          if (data.products && data.products.length > 0) {
            const product = data.products[0];
            const availableVariant = product.variants.find(v => v.available);
            return availableVariant ? availableVariant.id : null;
          }
        }
        return null;
      } catch (error) {
        return null;
      }
    }

    async addToCart(generationId) {
      const S = window.themeStrings.myGenerations;
      try {
        const generation = this.generations.find(g => g.id === generationId);
        if (!generation?.details) {
          this.showNotification(S.storyUnavailable, 'error');
          return false;
        }

        this.showNotification(S.addingToCart, 'info');

        // Get the product slug from the generation
        const productSlug = generation.storySlug;
        if (!productSlug) {
          this.showNotification(S.productSlugUnavailable, 'error');
          return false;
        }

        // Try direct product URL first
        const directProductUrl = canonicalUrl(`products/${productSlug}`);
        
        let productResponse = await fetch(`${directProductUrl}.js`);
        
        if (!productResponse.ok) {
          // Fallback to search if direct product not found
          const searchResponse = await fetch(canonicalUrl(`search?q=${productSlug}&type=product`));
          
          if (!searchResponse.ok) {
            this.showNotification(themeFmt(S.findProductFailed, { status: searchResponse.status }), 'error');
            return false;
          }

          const searchData = await searchResponse.text();
          
          const parser = new DOMParser();
          const searchDoc = parser.parseFromString(searchData, 'text/html');
          
          // Find the product URL from search results
          const productLink = searchDoc.querySelector(`a[href*="/products/${productSlug}"]`);
          if (!productLink) {
            this.showNotification(S.productNotFound, 'error');
            return false;
          }

          const productUrl = productLink.href;
          
          // Construct the correct product JSON URL
          const productJsonUrl = productUrl.replace(/\/$/, '') + '.js';
          
          productResponse = await fetch(productJsonUrl);
        }
        
        if (!productResponse.ok) {
          this.showNotification(themeFmt(S.getProductFailed, { status: productResponse.status }), 'error');
          return false;
        }

        // Check if product response is JSON
        const productContentType = productResponse.headers.get('content-type');
        
        let productData;
        try {
          productData = await productResponse.json();
        } catch (error) {
          const productResponseText = await productResponse.text();
          this.showNotification(S.productDataInvalid, 'error');
          return false;
        }
        
        // Get the first available variant
        const availableVariant = productData.variants.find(variant => variant.available);
        if (!availableVariant) {
          this.showNotification(S.noVariants, 'error');
          return false;
        }

        // Add to cart with the correct variant
        const formData = new FormData();
        formData.append('id', availableVariant.id);
        formData.append('quantity', '1');
        formData.append('properties[Kid Name]', generation.childName || '');
        formData.append('properties[Generation ID]', generationId);
       

        const cartResponse = await fetch(localeUrl('cart/add.js'), {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'application/json'
          }
        });

        // Try to parse JSON response
        let result;
        try {
          result = await cartResponse.json();
        } catch (error) {
          const responseText = await cartResponse.text();
          this.showNotification(S.cartResponseInvalid, 'error');
          return false;
        }

        if (cartResponse.ok) {
          this.showNotification(S.addedToCart, 'success');
          
          // Update cart count if available
          if (window.updateCartCount) {
            window.updateCartCount();
          }

          // Update cart state to reflect the new item
          await this.getCurrentCart();

          // Reload the page after successful cart addition
          setTimeout(() => {
            window.location.reload();
          }, 1500);

          return true;
        } else {
          this.showNotification(themeFmt(S.addFailed, { error: result.description || S.unknownError }), 'error');
          return false;
        }

      } catch (error) {
        this.showNotification(themeFmt(S.addError, { error: error.message }), 'error');
        return false;
      }
    }

    shareStory(generationId) {
      try {
        // Create the share URL with the generation ID
        const shareUrl = `https://preview.bysnaps.ai/pages/preview?generation_id=${generationId}`;
        
        // Check if Web Share API is available (mobile devices)
        if (navigator.share) {
          navigator.share({
            title: document.title,
            url: shareUrl
          }).catch((error) => {

            this.fallbackShare(shareUrl);
          });
        } else {
          // Fallback for desktop browsers
          this.fallbackShare(shareUrl);
        }
      } catch (error) {
        console.error('❌ Share process failed:', error);
        this.showNotification(window.themeStrings.myGenerations.shareFailed, 'error');
      }
    }

    fallbackShare(url) {
      try {
        // Create a temporary input element
        const tempInput = document.createElement('input');
        tempInput.value = url;
        document.body.appendChild(tempInput);
        
        // Select and copy the URL
        tempInput.select();
        tempInput.setSelectionRange(0, 99999); // For mobile devices
        document.execCommand('copy');
        
        // Remove the temporary element
        document.body.removeChild(tempInput);
        
        // Show success message
        this.showNotification(window.themeStrings.myGenerations.linkCopied, 'success');
      } catch (error) {
        console.error('❌ Fallback share failed:', error);
        // Last resort: open in new window
        window.open(url, '_blank');
        this.showNotification(window.themeStrings.myGenerations.linkOpened, 'info');
      }
    }

    showNotification(message, type = 'info') {
      // Remove any existing notifications
      const existingNotification = document.querySelector('.generations-notification');
      if (existingNotification) {
        existingNotification.remove();
      }

      const notification = document.createElement('div');
      notification.className = `generations-notification generations-notification--${type}`;
      notification.innerHTML = `
        <div class="notification-content">
          <div class="notification-message">${message}</div>
          <button class="notification-close" onclick="this.parentElement.parentElement.remove()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      `;

      // Add notification styles if not already present
      if (!document.querySelector('#generations-notification-styles')) {
        const styles = document.createElement('style');
        styles.id = 'generations-notification-styles';
        styles.textContent = `
          .generations-notification {
            position: fixed;
            top: 20px;
            right: 20px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            z-index: 10001;
            max-width: 400px;
            animation: notification-slide-in 0.3s ease-out;
          }
          
          .generations-notification--success {
            border-left: 4px solid #10b981;
          }
          
          .generations-notification--error {
            border-left: 4px solid #ef4444;
          }
          
          .generations-notification--info {
            border-left: 4px solid #3b82f6;
          }
          
          .notification-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px;
          }
          
          .notification-message {
            flex: 1;
            margin-right: 12px;
          }
          
          .notification-close {
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: background 0.2s;
          }
          
          .notification-close:hover {
            background: #f5f5f5;
          }
          
          @keyframes notification-slide-in {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `;
        document.head.appendChild(styles);
      }

      document.body.appendChild(notification);

      // Auto remove after 5 seconds
      setTimeout(() => {
        if (notification && notification.parentElement) {
          notification.remove();
        }
      }, 5000);
    }

    showLoading() {
      const loadingEl = document.getElementById('generations-loading');
      const emptyEl = document.getElementById('generations-empty');
      const tableEl = document.getElementById('generations-table');
      const errorEl = document.getElementById('generations-error');
      
      if (loadingEl) loadingEl.classList.remove('hidden');
      if (emptyEl) emptyEl.classList.add('hidden');
      if (tableEl) tableEl.classList.add('hidden');
      if (errorEl) errorEl.classList.add('hidden');
    }

    showEmpty() {
      const loadingEl = document.getElementById('generations-loading');
      const emptyEl = document.getElementById('generations-empty');
      const tableEl = document.getElementById('generations-table');
      const errorEl = document.getElementById('generations-error');
      
      if (loadingEl) loadingEl.classList.add('hidden');
      if (emptyEl) emptyEl.classList.remove('hidden');
      if (tableEl) tableEl.classList.add('hidden');
      if (errorEl) errorEl.classList.add('hidden');
    }

    showTable() {
      const loadingEl = document.getElementById('generations-loading');
      const emptyEl = document.getElementById('generations-empty');
      const tableEl = document.getElementById('generations-table');
      const errorEl = document.getElementById('generations-error');
      
      if (loadingEl) loadingEl.classList.add('hidden');
      if (emptyEl) emptyEl.classList.add('hidden');
      if (tableEl) tableEl.classList.remove('hidden');
      if (errorEl) errorEl.classList.add('hidden');
    }

    showError() {
      const loadingEl = document.getElementById('generations-loading');
      const emptyEl = document.getElementById('generations-empty');
      const tableEl = document.getElementById('generations-table');
      const errorEl = document.getElementById('generations-error');
      
      if (loadingEl) loadingEl.classList.add('hidden');
      if (emptyEl) emptyEl.classList.add('hidden');
      if (tableEl) tableEl.classList.add('hidden');
      if (errorEl) errorEl.classList.remove('hidden');
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Check if controller already exists
  if (!window.myGenerationsController && window.MyGenerationsController) {
    try {
      // Check if required elements exist before initializing
      const requiredElements = [
        'generations-loading',
        'generations-empty', 
        'generations-table',
        'generations-error'
      ];
      
      const allElementsExist = requiredElements.every(id => document.getElementById(id));
      
      if (allElementsExist) {
        window.myGenerationsController = new window.MyGenerationsController();
      } else {
        console.warn('MyGenerationsController: Required DOM elements not found, skipping initialization');
      }
    } catch (error) {
      console.error('MyGenerationsController initialization error:', error);
    }
  }
});